import React, { useCallback, useEffect, useState } from "react";
import "react-s-alert/dist/s-alert-css-effects/slide.css";
import "react-s-alert/dist/s-alert-default.css";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import Alert from "react-s-alert";
/********** COMPONENTS **********/
import UserAccount from "page/Security/Login/FormElements/UserAccount";
import LoadingSpinner from "component/LoadingSpinner";
import { ACCESS_TOKEN, publicMenu } from "constant/index";
import Template from "component/Template";
/********** REDUX **********/
import mapDispatchToProps from "./mapDispatchToProps";
import mapStateToProps from "./mapStateToProps";

/** Tiempo antes del vencimiento del JWT para mostrar el aviso (5 min). */
const SESSION_WARN_BEFORE_MS = 5 * 60 * 1000;

const getAccessTokenExpiryMs = () => {
  try {
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (!token || token.length === 0) return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1]));
    if (typeof payload.exp !== "number") return null;
    return payload.exp * 1000;
  } catch (e) {
    return null;
  }
};

const App = (props) => {
/********** VARIABLES **********/
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

/********** FUNCTIONS **********/
  const redirectPage = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN);
    props.LOGOUT();
    setAuthenticated(false);
    if (history.location.pathname !== "/") {
      history.replace("/");
    }
  }, [history, props]);

  /** Devuelve false si el token existía y era inválido o expirado (ya se llamó redirectPage). */
  const validateSession = useCallback(() => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (!token) {
      return true;
    }
    if (token.length === 0) {
      redirectPage();
      return false;
    }
    const expMs = getAccessTokenExpiryMs();
    if (expMs === null || expMs < Date.now()) {
      redirectPage();
      return false;
    }
    return true;
  }, [redirectPage]);

  useEffect(() => {
    window.addEventListener("popstate", validateSession);
    return () => window.removeEventListener("popstate", validateSession);
  }, [validateSession]);

  /** Aviso ~5 min antes del vencimiento y cierre al instante de expiración del JWT. */
  useEffect(() => {
    if (!props.authenticated) {
      return undefined;
    }
    const expMs = getAccessTokenExpiryMs();
    if (!expMs) {
      return undefined;
    }
    const now = Date.now();
    const msToLogout = expMs - now;
    const msToWarn = expMs - SESSION_WARN_BEFORE_MS - now;

    if (msToLogout <= 0) {
      redirectPage();
      return undefined;
    }

    const timeoutIds = [];

    if (msToWarn <= 0) {
      const mins = Math.max(1, Math.ceil(msToLogout / 60000));
      Alert.warning(
        `Tu sesión expirará en aproximadamente ${mins} minuto(s). Tomalo en cuenta y vuelve a iniciar sesión.`,
        { timeout: 12000 }
      );
    } else {
      timeoutIds.push(
        window.setTimeout(() => {
          Alert.warning(
            "Te quedan 5 minutos antes de que cierre la sesión.",
            { timeout: 12000 }
          );
        }, msToWarn)
      );
    }

    timeoutIds.push(
      window.setTimeout(() => {
        redirectPage();
      }, msToLogout)
    );

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [props.authenticated, redirectPage]);

  useEffect(() => {
    checkAutorization();
  }, []);

  const checkAutorization = async () => {
    setLoading(true);
    if (!validateSession()) {
      props.SET_MENU(publicMenu);
      setLoading(false);
      setAuthenticated(false);
      return;
    }
    let userAccount = new UserAccount();
    try {
      await userAccount.setCurrentUser();
      await userAccount.setMenu();
      if (userAccount.getIsError()) {
        Alert.error(userAccount.getErrorMessage());
        setLoading(false);
      } else {
        props.SET_MENU(userAccount.getMenu());
        props.SET_CURRENT_USER(userAccount.getCurrentUser());
        setLoading(false);
        setAuthenticated(userAccount.getAuthenticated())
      }
    } catch (error) {
      props.SET_MENU(publicMenu);
      setLoading(false);
    }
  }
/********** RENDER **********/
  if(loading){
    return(<LoadingSpinner open={loading}/>)
  }else{ return(
    <>
      <Template></Template>
      <Alert
        stack={{ limit: 1 }}
        timeout={3000}
        position="top-right"
        effect="slide"
        offset={65}
      />
    </>
  )}
}
export default connect(mapStateToProps, mapDispatchToProps)(App);